/**
 * The admin panel's look, in one place, so every tab and window matches.
 *
 * Class names only — nothing here changes what the panel does. Cards add their own
 * rounding and padding; everything else is used as-is.
 */
export function adminTheme(isDark) {
  const d = isDark;
  return {
    baseText: d ? "text-slate-100" : "text-slate-900",
    mutedText: d ? "text-slate-400" : "text-slate-500",

    /** The page behind everything. */
    page: d ? "bg-[#070d19] text-slate-100" : "bg-slate-50 text-slate-900",
    pageGlow: d
      ? "radial-gradient(1100px 520px at 12% -12%, rgba(114,234,213,0.09), transparent 60%), radial-gradient(900px 480px at 100% -8%, rgba(122,216,255,0.06), transparent 60%)"
      : "radial-gradient(1100px 520px at 12% -12%, rgba(114,234,213,0.16), transparent 60%), radial-gradient(900px 480px at 100% -8%, rgba(122,216,255,0.12), transparent 60%)",
    sidebar: d ? "border-white/[0.06] bg-[#0a1120]/80" : "border-slate-200/80 bg-white/80",

    /** A card: a tab adds rounded-2xl and its padding. */
    cardBase: d
      ? "border border-white/[0.07] bg-[#0d1524]/85 shadow-[0_1px_0_rgba(255,255,255,0.03)_inset,0_12px_32px_-18px_rgba(0,0,0,0.7)] backdrop-blur"
      : "border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_12px_32px_-20px_rgba(15,23,42,0.18)]",
    /** A panel inside a card: a list row, a note, a small stat. */
    inset: d ? "border border-white/[0.06] bg-white/[0.025]" : "border border-slate-200/80 bg-slate-50/70",
    rowBorder: d ? "border-white/[0.07]" : "border-slate-200/80",

    inputBase: d
      ? "border-white/10 bg-slate-950/50 text-slate-100 placeholder-slate-500 transition focus:border-accent-default focus:ring-2 focus:ring-accent-default"
      : "border-slate-200 bg-white text-slate-900 placeholder-slate-400 transition focus:border-accent-default focus:ring-2 focus:ring-accent-default",

    /** Tables: a bordered, rounded frame; a tinted head; rows that light up. */
    tableWrap: d
      ? "overflow-x-auto rounded-radius-sm border border-white/[0.07]"
      : "overflow-x-auto rounded-radius-sm border border-slate-200/80",
    thead: d ? "bg-white/[0.03] text-slate-400" : "bg-slate-50 text-slate-500",
    th: "px-4 py-2.5 text-caption-1-semibold uppercase tracking-wider",
    rowHover: d ? "transition-colors hover:bg-white/[0.02]" : "transition-colors hover:bg-slate-50/80",

    btnPrimary:
      "inline-flex items-center justify-center gap-2 rounded-radius-sm bg-accent-default px-4 py-2 text-subheadline-semibold text-accent-on shadow-sm transition hover:bg-mintStrong disabled:cursor-not-allowed disabled:opacity-50",
    btnSecondary: d
      ? "inline-flex items-center justify-center gap-2 rounded-radius-sm border border-white/10 bg-white/[0.03] px-3 py-2 text-subheadline font-medium text-slate-200 transition hover:bg-white/[0.07] disabled:opacity-40"
      : "inline-flex items-center justify-center gap-2 rounded-radius-sm border border-slate-200 bg-white px-3 py-2 text-subheadline font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-40",
    iconBtn: d
      ? "rounded-radius-xs border border-white/10 p-2 text-slate-300 transition hover:bg-white/[0.06] hover:text-white"
      : "rounded-radius-xs border border-slate-200 p-2 text-slate-600 transition hover:bg-slate-50 hover:text-slate-900",
    dangerIconBtn: "rounded-radius-xs border border-status-danger p-2 text-status-danger transition hover:bg-status-danger-bg",

    /** A card's heading. */
    heading: d ? "text-headline tracking-tight text-slate-100" : "text-headline tracking-tight text-slate-900",
  };
}

/** Tinted icon squares and figures for stat cards. */
export const ADMIN_TONES = {
  mint: { chip: "bg-accent-tinted text-accent-text ring-accent-default", text: "text-accent-text" },
  aqua: { chip: "bg-aqua/10 text-aqua ring-aqua/20", text: "text-aqua" },
  emerald: { chip: "bg-status-success-bg text-status-success ring-emerald-500/20", text: "text-status-success" },
  amber: { chip: "bg-status-warning-bg text-status-warning ring-amber-500/20", text: "text-status-warning" },
  rose: { chip: "bg-status-danger-bg text-status-danger ring-rose-500/20", text: "text-status-danger" },
  violet: { chip: "bg-violet-500/10 text-violet-300 ring-violet-500/20", text: "text-violet-300" },
};
